import AddIcon from '@mui/icons-material/Add';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import {
  Alert,
  Box,
  Button,
  Chip,
  IconButton,
  List,
  ListItem,
  ListItemText,
  MenuItem,
  Paper,
  Skeleton,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';

import { readContentLanguages, replaceContentLanguages } from '../../api/business/endpoints';
import { useAppMutation } from '../../api/useAppMutation';
import { usePermissions } from '../../auth/usePermissions';
import {
  addLanguage,
  isChanged,
  moveLanguage,
  newDefault,
  removedLanguages,
  removeLanguage,
} from '../../components/contentLanguagesDraft';
import { ErrorNotice } from '../../components/ErrorNotice';
import { languageName } from '../../components/localisedText';
import { PageHeader } from '../../components/PageHeader';
import { useLanguage } from '../../i18n/LanguageContext';

/**
 * The languages the business writes its product texts in (ADR-0010 §2, R0030 Q-03): add, reorder — the first
 * is the default — and remove. The API decides whether a new default has every text it needs, and says how
 * many are missing when it has not.
 */
export const ContentLanguagesPage = () => {
  const { language, t } = useLanguage();
  const permissions = usePermissions();
  const query = useQuery({ queryKey: ['content-languages'], queryFn: readContentLanguages });
  const [draft, setDraft] = useState<Array<string>>([]);
  const [adding, setAdding] = useState('');
  const [saved, setSaved] = useState(false);
  const canManage = permissions.writable && permissions.can('languages.manage');
  const save = useAppMutation(
    (languages: Array<string>) => replaceContentLanguages(languages),
    ['content-languages', 'products', 'product', 'option-groups'],
    () => setSaved(true),
  );

  useEffect(() => {
    if (query.data) {
      setDraft(query.data.languages);
    }
  }, [query.data]);

  const current = query.data?.languages ?? [];
  const available = query.data?.available ?? [];
  const nameOf = (code: string): string => languageName(code, available, language);
  const changed = isChanged(current, draft);
  const becomesDefault = newDefault(current, draft);
  const removed = removedLanguages(current, draft);
  const addable = available
    .filter((one) => !draft.includes(one.code))
    .sort((a, b) => a.names[language].localeCompare(b.names[language], language));
  const edit = (next: Array<string>): void => {
    setSaved(false);
    save.reset();
    setDraft(next);
  };

  return (
    <>
      <PageHeader title={t('languages.title')} subtitle={t('languages.subtitle')} />
      <ErrorNotice error={query.error} onRetry={() => void query.refetch()} />
      <Alert severity="info" variant="outlined" sx={{ mb: 3 }}>
        <Typography variant="body2">{t('languages.ruleDefault')}</Typography>
        <Typography variant="body2">{t('languages.ruleSwitch')}</Typography>
        <Typography variant="body2">{t('languages.ruleRemove')}</Typography>
      </Alert>
      <Paper variant="outlined" sx={{ p: 2, maxWidth: 640 }}>
        {query.isLoading ? (
          <Skeleton variant="rounded" height={160} />
        ) : (
          <Stack spacing={2}>
            <List dense disablePadding>
              {draft.map((code, index) => (
                <ListItem
                  key={code}
                  disableGutters
                  secondaryAction={
                    canManage ? (
                      <Stack direction="row" spacing={0.5}>
                        <Tooltip title={t('common.moveUp')}>
                          <span>
                            <IconButton
                              size="small"
                              disabled={save.isPending || index === 0}
                              onClick={() => edit(moveLanguage(draft, index, -1))}
                            >
                              <ArrowUpwardIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                        <Tooltip title={t('common.moveDown')}>
                          <span>
                            <IconButton
                              size="small"
                              disabled={save.isPending || index === draft.length - 1}
                              onClick={() => edit(moveLanguage(draft, index, 1))}
                            >
                              <ArrowDownwardIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                        <Tooltip
                          title={index === 0 ? t('languages.defaultNotRemovable') : t('languages.remove')}
                        >
                          <span>
                            <IconButton
                              size="small"
                              color="warning"
                              disabled={save.isPending || index === 0}
                              onClick={() => edit(removeLanguage(draft, code))}
                            >
                              <DeleteOutlineIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                      </Stack>
                    ) : undefined
                  }
                >
                  <ListItemText
                    primary={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {nameOf(code)}
                        <Typography component="span" variant="body2" color="text.secondary">
                          {code.toUpperCase()}
                        </Typography>
                        {index === 0 ? (
                          <Chip size="small" color="primary" label={t('languages.default')} />
                        ) : null}
                      </Box>
                    }
                  />
                </ListItem>
              ))}
            </List>
            {canManage ? (
              <Stack
                component="form"
                direction="row"
                spacing={1}
                onSubmit={(event) => {
                  event.preventDefault();
                  edit(addLanguage(draft, adding));
                  setAdding('');
                }}
              >
                <TextField
                  select
                  size="small"
                  fullWidth
                  label={t('languages.add')}
                  value={adding}
                  onChange={(event) => setAdding(event.target.value)}
                >
                  {addable.map((one) => (
                    <MenuItem key={one.code} value={one.code}>
                      {one.names[language]} ({one.code.toUpperCase()})
                    </MenuItem>
                  ))}
                </TextField>
                <Button type="submit" startIcon={<AddIcon />} disabled={adding === '' || save.isPending}>
                  {t('languages.addButton')}
                </Button>
              </Stack>
            ) : (
              <Typography variant="body2" color="text.secondary">
                {t('languages.readOnly')}
              </Typography>
            )}
            {becomesDefault !== null ? (
              <Alert severity="warning" variant="outlined">
                {t('languages.switchWarning', { name: nameOf(becomesDefault) })}
              </Alert>
            ) : null}
            {removed.length > 0 ? (
              <Alert severity="info" variant="outlined">
                {t('languages.removeNotice', { names: removed.map(nameOf).join(', ') })}
              </Alert>
            ) : null}
            <ErrorNotice error={save.error} />
            {saved && !changed ? <Alert severity="success">{t('languages.saved')}</Alert> : null}
            {canManage ? (
              <Stack direction="row" spacing={1} justifyContent="flex-end">
                <Button disabled={!changed || save.isPending} onClick={() => edit(current)}>
                  {t('common.cancel')}
                </Button>
                <Button
                  variant="contained"
                  disabled={!changed || save.isPending}
                  onClick={() => save.mutate(draft)}
                >
                  {t('common.save')}
                </Button>
              </Stack>
            ) : null}
          </Stack>
        )}
      </Paper>
    </>
  );
};
